import { t } from '@avalara/bat-apps-shared/i18n';
import { SCardAction, SCol, SIcon, SRow } from '@avalara/skylab-sdk/react';
import { useRef, useState } from 'react';

import { CheckItem } from '../../shared/accordion-styled-components';
import '../product-catalog.css';
import type { TranslationKey } from '@avalara/bat-apps-shared/i18n';
import type { ProductDetailSection, ProductPricingTier } from '../../../../../types/product-detail';
import { formatPrice } from '../../../../shared/helpers';
import AdminAccessDialog from './admin-access-dialog';
import { useIsBlockedFromPurchasing, usePricingCtaClick } from './use-pricing-cta';

type Props = {
  pricingTiers: ProductDetailSection<ProductPricingTier>;
  onContactClick: () => void;
  onOrderInProgress: () => void;
  // Gates the price table, the tier's own CTA/footer, and the premium tier's "contact us" CTA —
  // hidden for a viewer ineligible for online purchase (BRGUI-2737). Undefined/true renders
  // unchanged for every product that doesn't set this.
  isEligibleForOnlinePurchase?: boolean;
};

// Splits an originalPrice like "$291 Activation Fee | Waived" into the struck-through price/fee
// part and the plain suffix describing the waiver — the suffix isn't crossed out since it
// describes current, active state rather than something no longer in effect. Called on the
// already-t()-resolved display text, not the raw field — a product setting originalPrice to an
// i18n key (e.g. managed-returns.ts) would otherwise have this split the key string itself.
const splitOriginalPrice = (originalPrice: string) => {
  const [struckPrice, suffix] = originalPrice.split(' | ');
  return { struckPrice, suffix };
};

// Splits bodyText around bodyTextHighlight so only that phrase can be rendered at
// font-weight-semibold, leaving the rest of the sentence at normal weight. Returns null when no
// highlight is requested or the phrase isn't actually found in bodyText — either way, the caller
// falls back to today's full-.font-bold rendering rather than silently dropping text. Both
// arguments must already be t()-resolved — indexOf against a still-untranslated i18n key would
// never find the (also-untranslated) highlight key inside it.
const splitBodyTextHighlight = (bodyText: string, bodyTextHighlight?: string) => {
  if (!bodyTextHighlight) return null;

  const index = bodyText.indexOf(bodyTextHighlight);
  if (index === -1) return null;

  return {
    before: bodyText.slice(0, index),
    after: bodyText.slice(index + bodyTextHighlight.length),
  };
};

// tier.price is a plain string set server-side (e.g. '$970.2' in ecm-pro.ts), not a number, so it
// can't be passed to formatPrice directly — but some products use it for fully non-numeric copy
// instead (e.g. managed-returns.ts's 'Based on filing volume'/'Contact Sales'), which must render
// unchanged rather than as "$0.00" or "NaN". Reformats only when the *entire* trimmed string is a
// standalone number (optionally $-prefixed, with thousands separators/decimals) — a mixed string
// like 'Starting at $100 per month' must also render unchanged rather than collapsing to '$100.00'
// and losing the surrounding text, even though it contains a digit. Takes the already-t()-resolved
// display text — running this against a raw i18n key would never match the pattern.
const STANDALONE_PRICE_PATTERN = /^\$?-?\d[\d,]*(?:\.\d+)?$/;

const formatTierPrice = (price: string) => {
  const trimmedPrice = price.trim();
  if (!STANDALONE_PRICE_PATTERN.test(trimmedPrice)) return price;

  const numericPrice = Number(trimmedPrice.replace(/[$,]/g, ''));
  return Number.isFinite(numericPrice) ? formatPrice(numericPrice) : price;
};

const ProductDetailPricingTiers = ({
  pricingTiers: { heading, description, items },
  onContactClick,
  onOrderInProgress,
  isEligibleForOnlinePurchase = true,
}: Props) => {
  const handleCtaClick = usePricingCtaClick(onContactClick, onOrderInProgress);
  const isBlockedFromPurchasing = useIsBlockedFromPurchasing();
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  // A 'buy' click awaits checkOrderInProgress before navigating (usePricingCtaClick), leaving a
  // window where a second click (same tier or another) could fire before the first check settles
  // and double up on the resulting navigation/dialog. `isOrderCheckInFlightRef` blocks re-entry the
  // instant a click is handled -- refs update synchronously, unlike state, so it guards even the
  // click that lands before React has re-rendered the disabled button from the state below.
  // `isOrderCheckPending` state drives that visible disabled state. Shared across every tier
  // (not per-tier) since only one purchase attempt makes sense at a time; 'contact' ctas are
  // untouched by either -- see the ctaAction check in handleTierCtaClick.
  const isOrderCheckInFlightRef = useRef(false);
  const [isOrderCheckPending, setIsOrderCheckPending] = useState(false);

  // Only 'buy' ctas actually complete a purchase — 'contact'/'trial' ctas don't need admin
  // permission, so gating those too would block a non-admin from booking a sales call or
  // starting a trial for no reason tied to the dialog's own "complete purchases" copy. The admin
  // check runs first: a blocked non-admin sees the admin dialog immediately, without ever
  // starting (or being blocked by) an in-flight order-in-progress check.
  const handleTierCtaClick = async (id: string, tier: ProductPricingTier) => {
    if (tier.ctaAction === 'buy' && isBlockedFromPurchasing) {
      setAdminDialogOpen(true);
      return;
    }

    const guardsOrderCheck = tier.ctaAction === 'buy';
    if (guardsOrderCheck) {
      if (isOrderCheckInFlightRef.current) return;
      isOrderCheckInFlightRef.current = true;
      setIsOrderCheckPending(true);
    }

    try {
      await handleCtaClick(id, tier);
    } finally {
      if (guardsOrderCheck) {
        isOrderCheckInFlightRef.current = false;
        setIsOrderCheckPending(false);
      }
    }
  };

  if (!items.length) return null;

  return (
    <div id='buy-product-detail-pricing' data-ref='buy.product-detail.pricing-tiers-section'>
      <AdminAccessDialog open={adminDialogOpen} onDismiss={() => setAdminDialogOpen(false)} />
      <h2 className='product-detail-section-heading' data-ref='buy.product-detail.pricing.heading'>
        {heading ? t(heading as TranslationKey) : t('client.product-detail.pricing-heading')}
      </h2>
      {description && (
        <p className='pad-bottom-none' data-ref='buy.product-detail.pricing.subheading'>
          {t(description as TranslationKey)}
        </p>
      )}
      <SRow data-ref='buy.product-detail.pricing-tiers'>
        {items.map((tier) => {
          const resolvedBodyText = tier.bodyText ? t(tier.bodyText as TranslationKey) : undefined;
          const resolvedBodyTextHighlight = tier.bodyTextHighlight
            ? t(tier.bodyTextHighlight as TranslationKey)
            : undefined;
          const bodyTextHighlight = resolvedBodyText
            ? splitBodyTextHighlight(resolvedBodyText, resolvedBodyTextHighlight)
            : null;
          const originalPrice = tier.originalPrice
            ? splitOriginalPrice(t(tier.originalPrice as TranslationKey))
            : undefined;
          // A 'buy' tier can reach the client with no offerFlow (e.g. ecm-pro.ts/avalara-tax-research.ts
          // fall back to `offerFlow: undefined` when the live offer lookup doesn't resolve one) —
          // handleCtaClick silently no-ops in that case, so disable the CTA rather than leave the
          // customer clicking a live-looking button with no feedback. tier.ctaDisabled is set
          // server-side (gateAlreadyPurchasedCta) when the account already owns this offer — the
          // footer still renders (unlike isEligibleForOnlinePurchase below), just with this button
          // disabled and its ctaText already overridden to "Already purchased".
          const isCtaDisabled = tier.ctaDisabled || (tier.ctaAction === 'buy' && !tier.offerFlow);

          return (
            <SCol
              className={`${tier.id.startsWith('managed-returns') ? '' : 'pad-all-none'}${tier.offset ? ' pad-left-md' : ''}`}
              key={tier.id}
              span='auto'
              data-ref={`buy.product-detail.pricing-tier.${tier.id}`}
            >
              <SCardAction
                className={`product-detail-pricing-tier${tier.highlighted ? ' product-detail-pricing-tier--highlighted' : ''}`}
              >
                {tier.featuredLabel && (
                  <span slot='status' className='product-detail-pricing-tier-featured-label'>
                    {t(tier.featuredLabel as TranslationKey)}
                  </span>
                )}
                <h3 slot='title'>{t(tier.name as TranslationKey)}</h3>
                {tier.subtitle && (
                  <h3 className='margin-top-md' slot='subtitle'>
                    {t(tier.subtitle as TranslationKey)}
                  </h3>
                )}
                <div slot='description'>
                  {/* bodyText ("Contact for pricing"-style copy) is purchase-related, so it hides
                      along with the rest of this section's price/CTA content for a product whose
                      viewer is ineligible for online purchase (BRGUI-2737). Applies to every tier
                      uniformly -- eligibility is a per-product/account gate, not a per-tier one. */}
                  {resolvedBodyText && isEligibleForOnlinePurchase && (
                    <h3 className='product-detail-pricing-tier-body-text'>
                      {bodyTextHighlight ? (
                        <>
                          {bodyTextHighlight.before}
                          <span className='product-detail-pricing-tier-body-text-highlight'>
                            {resolvedBodyTextHighlight}
                          </span>
                          {bodyTextHighlight.after}
                        </>
                      ) : (
                        <span className='font-bold'>{resolvedBodyText}</span>
                      )}
                    </h3>
                  )}
                  {tier.bodyDescriptionText && (
                    <h3 className='product-detail-pricing-tier-body-text'>
                      <span>{t(tier.bodyDescriptionText as TranslationKey)}</span>
                    </h3>
                  )}
                  {/* The price line is purchase-related, so it hides along with the price table/
                      footer/CTA for an ineligible viewer (BRGUI-2737); applies uniformly to every
                      tier, same reasoning as bodyText above. */}
                  {tier.price && isEligibleForOnlinePurchase && (
                    <p data-ref={`buy.product-detail.pricing-tier.${tier.id}.price`}>
                      <span className='text-xxl font-bold'>{formatTierPrice(t(tier.price as TranslationKey))}</span>
                      {tier.priceSubtext && <span>{t(tier.priceSubtext as TranslationKey)}</span>}
                    </p>
                  )}
                  {/* originalPrice/badge are purchase-related (a struck-through list price and a
                      discount callout), so they hide along with the rest of this section's price/
                      CTA content for an ineligible viewer, same reasoning as bodyText/price above. */}
                  {originalPrice && isEligibleForOnlinePurchase && (
                    <span className='product-detail-pricing-tier-original-price'>
                      <span className='product-detail-pricing-tier-original-price-struck'>
                        {originalPrice.struckPrice}
                      </span>
                      {originalPrice.suffix && ` | ${originalPrice.suffix}`}
                    </span>
                  )}
                  {tier.badge && isEligibleForOnlinePurchase && (
                    <span
                      className='product-detail-pricing-tier-badge'
                      data-ref={`buy.product-detail.pricing-tier.${tier.id}.badge`}
                    >
                      {t(tier.badge as TranslationKey)}
                    </span>
                  )}

                  {tier.bullets && (
                    <>
                      <h2 className='margin-top-sm'>{t('client.product-detail.pricing-tier.bullets-heading')}</h2>
                      {tier.bullets?.map((bullet) => (
                        <CheckItem key={bullet} fontSize='14px'>
                          <SIcon
                            className='product-detail-pricing-tier-bullets'
                            name='check-circle'
                            aria-hidden='true'
                          />
                          <span>{t(bullet as TranslationKey)}</span>
                        </CheckItem>
                      ))}
                    </>
                  )}
                  {tier.priceTable && isEligibleForOnlinePurchase && (
                    <table
                      className='product-detail-pricing-tier-price-table'
                      data-ref={`buy.product-detail.pricing-tier.${tier.id}.price-table`}
                    >
                      <thead>
                        <tr>
                          <th>{t('client.product-detail.price-table.range-header')}</th>
                          <th>{t('client.product-detail.price-table.price-header')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tier.priceTable.map((row) => (
                          <tr key={row.range}>
                            <td>{row.range}</td>
                            <td>
                              {row.originalPrice && (
                                <>
                                  <span className='product-detail-pricing-tier-original-price-struck'>
                                    {row.originalPrice}
                                  </span>{' '}
                                  &rarr;{' '}
                                </>
                              )}
                              {row.price}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {tier.overageNote && isEligibleForOnlinePurchase && (
                    <p
                      className='text-sm product-detail-pricing-tier-overage-note'
                      data-ref={`buy.product-detail.pricing-tier.${tier.id}.overage-note`}
                    >
                      {t(tier.overageNote as TranslationKey)}
                    </p>
                  )}
                </div>
                {/* The footer (CTA + finePrint, both purchase-related -- "purchase button or
                    Schedule a call button" per BRGUI-2737) is hidden entirely for an ineligible
                    viewer, uniformly for every tier -- same reasoning as bodyText/price above. */}
                {isEligibleForOnlinePurchase && (
                  <div slot='footer' className='flex flex-dir-col full-width'>
                    {tier.finePrint && (
                      <p className='text-sm product-detail-pricing-tier-fine-print'>
                        {t(tier.finePrint as TranslationKey)}
                      </p>
                    )}
                    <button
                      type='button'
                      className={tier.highlighted ? 'primary' : 'secondary'}
                      data-ref={`buy.product-detail.pricing-tier.${tier.id}.cta`}
                      onClick={() => handleTierCtaClick(tier.id, tier)}
                      disabled={isCtaDisabled || (tier.ctaAction === 'buy' && isOrderCheckPending)}
                    >
                      {t(tier.ctaText as TranslationKey)}
                    </button>
                  </div>
                )}
              </SCardAction>
            </SCol>
          );
        })}
      </SRow>
    </div>
  );
};

export default ProductDetailPricingTiers;
